import { request } from './client';

/**
 * Where you stand this week, in every game you are still alive in.
 *
 * A second call rather than more fields on `getMyGames`: that one lists everything you
 * are in, archived seasons included, and this is one row per live game. Facts rather
 * than sentences - whether "Picked: Brighton" or "next sheet not up yet" is the right
 * thing to say is a question for the screen, and both wordings already exist on the
 * game pages.
 *
 * Only ever your own pick. Game Progress is the endpoint for what everybody else did,
 * and it conceals a live round for exactly that reason.
 */
export function getMyWeek() {
  return request('/api/customer/games/my-week');
}

export function getMyGames() {
  return request('/api/customer/games/my-games');
}

// inviteePlayerIds get an invitation to accept or decline once the game exists.
export function createGame({ name, tournamentId, startingMatchWeek, gafferEnabled = false, inviteePlayerIds = [] }) {
  return request('/api/customer/games', {
    method: 'POST',
    body: { name, tournamentId, startingMatchWeek, gafferEnabled, inviteePlayerIds },
  });
}

/**
 * Joins a game by its code. Answers the game when it seats you - or, when the game has just
 * rolled over, the request it raised for the manager to approve instead (backlog item 49).
 * `JoinGameModal` tells the two apart.
 */
export function joinGame(gameCode) {
  return request('/api/customer/games/join', { method: 'POST', body: { gameCode } });
}

/** Who is asking to join a game after its rollover. Its manager only. */
export function getJoinRequests(gameId) {
  return request(`/api/customer/games/${gameId}/join-requests`);
}

/** Lets them in, level with everybody. In a league's game they become a member too. */
export function approveJoinRequest(gameId, requestId) {
  return request(`/api/customer/games/${gameId}/join-requests/${requestId}/approve`, { method: 'POST' });
}

export function declineJoinRequest(gameId, requestId) {
  return request(`/api/customer/games/${gameId}/join-requests/${requestId}/decline`, { method: 'POST' });
}

/**
 * Says what a code names before anything is joined: {kind} is 'GAME', 'LEAGUE'
 * or 'UNKNOWN'. Game codes and league codes look identical, so resolving first
 * is what lets the join box ask the right question instead of guessing.
 *
 * A GAME result carries leagueId/leagueName when that game sits under a league.
 * Joining such a game makes you a member of that league too, which is the bit
 * worth saying out loud before it happens.
 * UNKNOWN comes back as a normal 200, so only real failures throw.
 */
export function resolveJoinCode(code) {
  return request(`/api/customer/join-codes/${encodeURIComponent(code)}`);
}

/**
 * Grows a mini league out of this game. What happens depends on who asks, and
 * the response says which via `adoptedGame`: the game's manager converts the
 * game itself, carrying its players and its points into the new league, while
 * anyone else gets a brand new empty league of their own and this game is left
 * alone. Returns {leagueId, leagueName, leagueCode, adoptedGame}.
 */
export function createLeagueFromGame(gameId, name) {
  return request(`/api/customer/games/${gameId}/mini-league`, {
    method: 'POST',
    body: { name },
  });
}

export function getGame(gameId) {
  return request(`/api/games/${gameId}`);
}

export function removePlayer(gameId, playerId) {
  return request(`/api/customer/games/${gameId}/players/${playerId}`, { method: 'DELETE' });
}

/** Manager only, and only while the game is still open to joiners. */
export function addGaffer(gameId) {
  return request(`/api/customer/games/${gameId}/gaffer`, { method: 'POST' });
}

/** Manager only. Allowed at any time; his past picks stay on the record. */
export function removeGaffer(gameId) {
  return request(`/api/customer/games/${gameId}/gaffer`, { method: 'DELETE' });
}

export function getFixtures(gameId, gameWeek) {
  return request(`/api/customer/games/${gameId}/gameweek/${gameWeek}/fixtures`);
}

export function getUsedTeams(gameId, playerId) {
  return request(`/api/customer/games/${gameId}/players/${playerId}/used-teams`);
}

export function getSelections(gameId, playerId) {
  return request(`/api/customer/games/${gameId}/players/${playerId}/selections`);
}

export function getGameweekStatus(gameId, playerId) {
  return request(`/api/customer/games/${gameId}/players/${playerId}/gameweek-status`);
}

export function selectTeam(gameId, playerId, { gameFixtureEntryId, teamId }) {
  return request(`/api/customer/games/${gameId}/players/${playerId}/select-team`, {
    method: 'POST',
    body: { gameFixtureEntryId, teamId },
  });
}
